"use client";

import { useState, useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

// Position before paint on the client (avoids a visible jump), plain effect on
// the server to dodge React's useLayoutEffect-during-SSR warning.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
import { ChevronDown } from "@/lib/icons";

interface Option {
  value: string;
  label: string;
  /** Optional glyph shown before the label (e.g. a broker logo). */
  icon?: ReactNode;
  disabled?: boolean;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  className?: string;
  panelClassName?: string;
}

export default function CustomSelect({
  value,
  onChange,
  options,
  className = "",
  panelClassName = "",
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Keep the dropdown on-screen: its bottom must stay at least 5px above the
  // viewport bottom. If it doesn't fit below the button, shift it up; if it's
  // still taller than the available space, cap the height so it scrolls.
  useIsoLayoutEffect(() => {
    if (!open || !panelRef.current) { setMaxHeight(undefined); return; }
    const MARGIN = 5;
    const vh = window.innerHeight;
    const panelH = panelRef.current.scrollHeight;
    let top = pos.top;
    if (top + panelH > vh - MARGIN) {
      top = Math.max(MARGIN, vh - MARGIN - panelH);
    }
    const avail = vh - MARGIN - top;
    setMaxHeight(panelH > avail ? avail : undefined);
    if (top !== pos.top) setPos((p) => ({ ...p, top }));
  }, [open, pos.top]);

  // Dismiss on ANY click anywhere (including inside modals that stopPropagation
  // on bubble). Capture phase hears the event before stopPropagation; setTimeout
  // lets option/button handlers finish (select value, toggle) first.
  useEffect(() => {
    if (!open) return;
    const dismiss = () => {
      setTimeout(() => setOpen(false), 0);
    };
    const id = requestAnimationFrame(() => {
      document.addEventListener("click", dismiss, true);
    });
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener("click", dismiss, true);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 4, left: r.left });
          }
          setOpen(!open);
        }}
        className={`flex items-center gap-1 whitespace-nowrap text-white hover:text-white border border-muted rounded px-3 py-1.5 bg-surface transition-colors text-xs ${className}`}
      >
        {selected?.icon}
        {selected?.label ?? value}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className={`dropdown-panel fixed ${panelClassName}`}
          // A flex column sized to its widest row. Items must not use a
          // percentage width here: "100% of a max-content parent" is circular
          // and Safari resolves it by narrowing the panel until labels wrap.
          style={{ top: pos.top, left: pos.left, zIndex: 90, maxHeight, overflowY: maxHeight != null ? "auto" : undefined, display: "flex", flexDirection: "column", alignItems: "stretch", width: "max-content" }}
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              disabled={o.disabled}
              onClick={() => {
                if (o.disabled) return;
                onChange(o.value);
                setOpen(false);
              }}
              className={`dropdown-item ${o.disabled ? "!text-muted/40 !cursor-default" : ""}`}
              // Inline, because the stylesheet's .dropdown-item sets display:block
              // and Tailwind's preflight makes <img> a block: together they put an
              // icon on one line and the label on the next. A flex row keeps
              // icon and label side by side whatever the stylesheet says.
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                width: "auto",
                whiteSpace: "nowrap",
                ...(o.value === value ? { color: "#6ee7b7" } : {}),
              }}
            >
              {o.icon}
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
